import cx from 'classnames'
import { type ReactNode, type PointerEventHandler, type Ref } from 'react'
import { match } from '@/utils/pattern-match-util'
import styles from './InputShell.module.css'
import {
  IsSkeletonContext,
  Skeleton,
  useIsSkeleton,
} from '@/components/Skeleton'
import { useSkeletonListContext } from '@/components/Skeleton/SkeletonList'

/**
 * Variants for the input shell visual.
 */
export type InputShellVariant = 'default' | 'embedded'

/**
 * Props that the direct consumer of InputShell
 * can accept and forward as-is.
 */
export interface InputShellInheritedProps {
  /**
   * Variant to use.
   */
  variant?: InputShellVariant
}

export interface InputShellProps extends InputShellInheritedProps {
  /**
   * Pointer down event.
   */
  onPointerDown?: PointerEventHandler<HTMLDivElement>
  /**
   * Pointer up event.
   */
  onPointerUp?: PointerEventHandler<HTMLDivElement>
  /**
   * Whether the form control is in an invalid state.
   */
  invalid?: boolean
  /**
   * Whether the control is disabled.
   */
  isDisabled?: boolean
  /**
   * Whether the control is focused.
   */
  isFocused?: boolean
  /**
   * Whether this represents a text field, resulting in using a text cursor.
   */
  isTextField?: boolean
  /**
   * Whether a skeleton overlay should be rendered.
   */
  isSkeleton?: boolean
  /**
   * Can be used to render slots.
   */
  children?: ReactNode
  /**
   * Ref to the root element.
   */
  ref?: Ref<HTMLDivElement>
  /**
   * Test ID attribute.
   */
  testId?: string
}

/**
 * Input shell root element.
 */
export function InputShellRoot({
  invalid,
  children,
  onPointerDown,
  onPointerUp,
  isDisabled,
  isFocused,
  isTextField,
  isSkeleton,
  ref,
  variant = 'default',
  testId,
}: InputShellProps) {
  isSkeleton = useIsSkeleton(isSkeleton)
  return (
    <IsSkeletonContext value={isSkeleton}>
      <div
        ref={ref}
        onPointerDown={onPointerDown}
        onPointerUp={onPointerUp}
        data-test={testId}
        className={cx(
          styles.root,
          invalid && styles.invalid,
          isDisabled && styles.disabled,
          isFocused && styles.focused,
          isTextField && styles.textCursor,
          isSkeleton && styles.skeleton,
          variant === 'embedded' && styles.embedded,
        )}
      >
        {children}
      </div>
    </IsSkeletonContext>
  )
}

interface InputShellControlProps {
  children?: ReactNode
  pad?: boolean
  isSkeleton?: boolean
  onPointerDown?: PointerEventHandler
}

/**
 * Input Shell Control
 */
export function InputShellControl({
  children,
  pad,
  isSkeleton,
  onPointerDown,
}: InputShellControlProps) {
  isSkeleton = useIsSkeleton(isSkeleton)
  const { varyPercent } = useSkeletonListContext()
  return (
    <div
      className={cx(styles.control, (pad || isSkeleton) && styles.pad)}
      onPointerDown={onPointerDown}
    >
      {isSkeleton ? (
        <>
          <Skeleton width={varyPercent(40)} />
          {/* We need to still render the element in case the
            skeleton state is contingent on it. */}
          <div className={styles.hidden}>{children}</div>
        </>
      ) : (
        children
      )}
    </div>
  )
}

/**
 * Which side of the input the slot should be on.
 */
export type SlotSide = 'left' | 'right'

export interface SlotProps {
  side?: SlotSide
  children: ReactNode
}

/**
 * Provides a slot before or after the main field within the control.
 */
export function InputShellSlot({ children, side = 'left' }: SlotProps) {
  return (
    <div
      data-slot-side={side}
      className={cx(styles.slot, getSlotSideClasses(side))}
    >
      {children}
    </div>
  )
}

const getSlotSideClasses = (side: SlotSide) =>
  match(side, {
    left: styles.slotLeft,
    right: styles.slotRight,
  })
